/**
 * Process-local cache for the `nodesMetadata.json` sidecar plus the
 * save-flow helpers that depend on it.
 *
 * Why a cache: every draft save needs the *other* mode branch (draft save
 * must not erase the published snapshot, and vice versa). Without a cache
 * the save path GETs the sidecar before every write — one extra VFS
 * roundtrip (~150-300ms in cloud) on every Cmd+S. The sidecar is already
 * fetched once at workflow load; stashing it here lets the save path read
 * synchronously and lets the sidecar PUT run in parallel with the workflow
 * PUT instead of strictly after it.
 *
 * Lifecycle (not persisted across reloads — page load reseeds):
 *   - Page load: `setCachedSidecar(key, fetched)`
 *   - Save success: `commitSidecarPlan(plan)` refreshes the cache
 *   - Workflow delete: `deleteCachedSidecar(key)`
 *   - Sign-out: `clearSidecarCache()`
 */

import {
  buildSidecar,
  emptyNodesMetadata,
  type NodesMetadataFile,
} from './nodesMetadata';
import type { WorkflowMode } from '@/stores/workflowLayoutStore';
import {
  composeWorkflowKey,
  type WorkflowIdentity,
} from '@/stores/workflowViewPrefsStore';
import type { WorkflowDefinition } from '@/types/workflowDefinition';

const cache = new Map<string, NodesMetadataFile>();

export function getCachedSidecar(key: string): NodesMetadataFile | undefined {
  return cache.get(key);
}

export function setCachedSidecar(key: string, file: NodesMetadataFile): void {
  cache.set(key, file);
}

export function deleteCachedSidecar(key: string): void {
  cache.delete(key);
}

export function clearSidecarCache(): void {
  cache.clear();
}

/**
 * A pre-built sidecar write paired with its cache key. Save sites build a
 * plan, hand the `file` to `saveNodesMetadata`, and call
 * {@link commitSidecarPlan} on success to keep the cache in sync.
 */
export interface SidecarPlan {
  file: NodesMetadataFile;
  key: string;
}

const previousFromCache = (key: string): NodesMetadataFile =>
  getCachedSidecar(key) ?? emptyNodesMetadata();

/**
 * Build a sidecar write for a draft save, folding the current in-memory
 * workflow into `mode` and preserving the other branch from cache.
 * Returns `null` when there's no workflow identity yet — callers should
 * treat that as "skip the sidecar write".
 */
export function planDraftSidecar(
  workflow: WorkflowDefinition | null,
  identity: WorkflowIdentity | null,
  mode: WorkflowMode = 'draft',
): SidecarPlan | null {
  if (!identity) return null;
  const key = composeWorkflowKey(identity);
  const file = buildSidecar(workflow, identity, mode, previousFromCache(key));
  return { file, key };
}

/**
 * Build a sidecar write for publish. Folds the current snapshot into BOTH
 * branches: `published` first (so readers of the published view see the
 * just-released layout) then `draft` on top of that (so a re-edit right
 * after publish doesn't start from a stale draft snapshot).
 */
export function planPublishSidecar(
  workflow: WorkflowDefinition | null,
  identity: WorkflowIdentity | null,
): SidecarPlan | null {
  if (!identity) return null;
  const key = composeWorkflowKey(identity);
  const published = buildSidecar(workflow, identity, 'published', previousFromCache(key));
  const file = buildSidecar(workflow, identity, 'draft', published);
  return { file, key };
}

/** Write-through: refresh the cache so the next save also skips the GET. */
export function commitSidecarPlan(plan: SidecarPlan): void {
  setCachedSidecar(plan.key, plan.file);
}
